import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, ShieldCheck, Bell, LogOut, LogIn, ArrowLeftRight, ChevronLeft, LayoutDashboard, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const nav = useNavigate();
  const { user, logout } = useAuth();

  if (user === null) {
    return <div dir="rtl" className="p-8 text-center" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>;
  }

  if (user === false) {
    return (
      <div dir="rtl">
        <div className="sticky top-0 z-20 px-4 py-3.5" style={{ background: "var(--k-paper)", borderBottom: "1px solid var(--k-line)" }}>
          <h1 className="font-head text-[17px] font-bold">حسابي</h1>
        </div>
        <div className="text-center pt-20 px-8" style={{ color: "var(--k-ink-soft)" }}>
          <LogIn size={44} className="mx-auto mb-3 opacity-50" />
          <p className="text-[13.5px] mb-5 leading-relaxed">سجّل الدخول للوصول إلى طلباتك وواجهة التاجر</p>
          <button data-testid="go-login-btn" onClick={() => nav("/auth")} className="k-tap px-6 py-3 rounded-2xl text-white font-bold text-[13.5px]" style={{ background: "var(--k-ink)" }}>تسجيل الدخول / حساب جديد</button>
        </div>

        <div className="rounded-2xl mx-4 mt-8 p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <h3 className="font-head text-[14px] font-bold mb-2.5">اتصل بنا</h3>
          <div className="flex flex-col gap-2 text-[13px]">
            <a href="tel:0913259296" data-testid="guest-contact-phone" className="flex items-center justify-between gap-2 pb-2 mb-1" style={{ color: "var(--k-ink)", borderBottom: "1px solid var(--k-line)" }}><span className="flex items-center gap-2 font-num"><Phone size={15} style={{ color: "var(--k-gold-deep)" }} /> 0913259296</span><span className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>رقم موحّد لكل الأقسام</span></a>
            <a href="mailto:info@kazuoni.com" data-testid="guest-contact-info" className="flex items-center gap-2" style={{ color: "var(--k-ink)" }}><Mail size={15} style={{ color: "var(--k-gold-deep)" }} /> info@kazuoni.com</a>
            <a href="mailto:sales@kazuoni.com" data-testid="guest-contact-sales" className="flex items-center gap-2" style={{ color: "var(--k-ink)" }}><Mail size={15} style={{ color: "var(--k-gold-deep)" }} /> sales@kazuoni.com</a>
          </div>
        </div>
      </div>
    );
  }

  const doLogout = async () => { await logout(); toast.success("تم تسجيل الخروج"); nav("/"); };

  const rows = [
    { icon: LayoutDashboard, label: "لوحتي", onClick: () => nav("/dashboard"), testid: "menu-dashboard" },
    { icon: Box, label: "طلباتي", onClick: () => nav("/orders"), testid: "menu-orders" },
    { icon: ShieldCheck, label: "التحقق والتوثيق", onClick: () => nav("/merchant"), testid: "menu-verify" },
    { icon: Bell, label: "الإشعارات", onClick: () => nav("/notifications"), testid: "menu-notif" },
  ];
  if (user?.role === "admin") rows.push({ icon: ShieldCheck, label: "لوحة الإدارة", onClick: () => nav("/admin"), testid: "menu-admin" });

  return (
    <div dir="rtl">
      <div className="sticky top-0 z-20 px-4 py-3.5" style={{ background: "var(--k-paper)", borderBottom: "1px solid var(--k-line)" }}>
        <h1 className="font-head text-[17px] font-bold">حسابي</h1>
      </div>

      <div className="flex items-center gap-3 px-4 py-4">
        <div className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center text-white font-head font-bold text-[18px]" style={{ background: "var(--k-ink)" }}>
          {user.avatar ? <img src={user.avatar.startsWith("http") ? user.avatar : `${process.env.REACT_APP_BACKEND_URL}${user.avatar}`} alt="avatar" className="w-full h-full object-cover" /> : (user.name || "؟").trim().charAt(0)}
        </div>
        <div>
          <p className="font-head text-[15px] font-bold" data-testid="account-name">{user.name}</p>
          <p className="text-[12px] font-num" style={{ color: "var(--k-ink-soft)", direction: "ltr", textAlign: "right" }}>{user.email}</p>
          {user.role === "admin" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 inline-block" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>مدير النظام</span>}
          {Number(user.balance || 0) > 0 && <p data-testid="account-balance" className="text-[12px] font-bold mt-1" style={{ color: "var(--k-emerald)" }}>الرصيد: <span className="font-num">{Number(user.balance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> د.ل</p>}
        </div>
      </div>

      <div className="rounded-2xl mx-4 overflow-hidden" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        {rows.map((r, i) => {
          const Icon = r.icon;
          return (
            <button key={i} data-testid={r.testid} onClick={r.onClick} className="k-tap w-full flex items-center gap-3 px-4 py-3.5 text-[13.5px] text-right" style={{ borderBottom: i < rows.length - 1 ? "1px solid var(--k-line)" : "none" }}>
              <Icon size={18} style={{ color: "var(--k-ink-soft)" }} />
              <span className="flex-1">{r.label}</span>
              <ChevronLeft size={16} style={{ color: "var(--k-ink-soft)" }} />
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mx-4 mt-4 p-4 rounded-2xl" style={{ background: "var(--k-ink)", color: "#fff" }}>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(217,119,6,.22)", color: "var(--k-gold)" }}><ArrowLeftRight size={19} /></span>
        <span className="flex-1"><strong className="font-head text-[13.5px] block">واجهة التاجر</strong><small className="text-[11.5px] opacity-75">إدارة منتجاتك ومبيعاتك</small></span>
        <button data-testid="open-merchant-btn" onClick={() => nav("/merchant")} className="k-tap px-4 py-2 rounded-xl font-bold text-[13px]" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>فتح</button>
      </div>

      <div className="rounded-2xl mx-4 mt-4 p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <h3 className="font-head text-[14px] font-bold mb-2.5">اتصل بنا</h3>
        <div className="flex flex-col gap-2 text-[13px]">
          <a href="tel:0913259296" data-testid="account-contact-phone" className="flex items-center justify-between gap-2 pb-2 mb-1" style={{ color: "var(--k-ink)", borderBottom: "1px solid var(--k-line)" }}><span className="flex items-center gap-2 font-num"><Phone size={15} style={{ color: "var(--k-gold-deep)" }} /> 0913259296</span><span className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>رقم موحّد لكل الأقسام</span></a>
          <a href="mailto:info@kazuoni.com" data-testid="account-contact-info" className="flex items-center gap-2" style={{ color: "var(--k-ink)" }}><Mail size={15} style={{ color: "var(--k-gold-deep)" }} /> info@kazuoni.com</a>
          <a href="mailto:sales@kazuoni.com" data-testid="account-contact-sales" className="flex items-center gap-2" style={{ color: "var(--k-ink)" }}><Mail size={15} style={{ color: "var(--k-gold-deep)" }} /> sales@kazuoni.com</a>
        </div>
      </div>

      <div className="px-4 mt-4 pb-8">
        <button data-testid="logout-btn" onClick={doLogout} className="k-tap w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-[13.5px]" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)", color: "var(--k-ruby)" }}>
          <LogOut size={16} /> تسجيل الخروج
        </button>
      </div>
    </div>
  );
}
